import React from "react";
import { FormSection } from "../../../common/FormComponents";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import Tooltip from "../../../common/FormComponents/Tooltip";

type PaymentInfo = {
  paymentMethod: string;
  paymentDetails: {
    autoRenew: boolean;
    isAuthorized: boolean;
    consentToEmails: boolean;
    agreeToTerms: boolean;
    invoicePaymentMethod: string;
  };
};

type PaymentInfoSectionProps = {
  paymentInfo: PaymentInfo;
  onPaymentInfoChange: ({
    field,
    value,
  }: {
    field: keyof PaymentInfo;
    value: string;
  }) => void;
  onPaymentDetailsChange: ({
    field,
    value,
  }: {
    field: keyof PaymentInfo["paymentDetails"];
    value: boolean | string;
  }) => void;
  errors?: Record<string, string>;
};

const invoicePaymentMethods = [
  { value: "creditCard", label: "Credit Card" },
  { value: "eTransfer", label: "eTransfer" },
  { value: "check", label: "Cheque" },
  { value: "bankTransfer", label: "Bank Transfer" },
];

const PaymentInfoSection = ({
  paymentInfo,
  onPaymentInfoChange,
  onPaymentDetailsChange,
  errors = {},
}: PaymentInfoSectionProps) => {
  const handlePaymentMethodChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onPaymentInfoChange({ field: "paymentMethod", value: e.target.value });
  };

  const handleInvoiceMethodChange = (e: SelectChangeEvent<string>) => {
    onPaymentDetailsChange({
      field: "invoicePaymentMethod",
      value: e.target.value,
    });
  };

  const handleCheckboxChange =
    (field: keyof PaymentInfo["paymentDetails"]) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onPaymentDetailsChange({ field, value: e.target.checked });
    };

  const showInvoiceOptions = paymentInfo.paymentMethod === "invoice";

  return (
    <FormSection
      title="Payment Information"
      tooltipText="Select payment method and agreement to terms"
    >
      <div className="space-y-6 flex flex-col gap-y-6">
        <FormControl component="fieldset">
          <div className="flex items-center mb-2">
            <FormLabel
              component="legend"
              className="text-sm font-medium text-gray-900"
            >
              Payment Method
            </FormLabel>
            <Tooltip content="Select how the membership will be paid for" />
          </div>
          <RadioGroup
            name="paymentMethod"
            value={paymentInfo.paymentMethod}
            onChange={handlePaymentMethodChange}
          >
            <FormControlLabel
              value="creditCard"
              control={<Radio />}
              label="Credit Card"
            />
            <FormControlLabel
              value="invoice"
              control={<Radio />}
              label="Invoice Me"
            />
          </RadioGroup>
          {errors.paymentMethod && (
            <p className="text-red-500 text-xs mt-1">{errors.paymentMethod}</p>
          )}
        </FormControl>

        {showInvoiceOptions && (
          <div className="ml-8 mt-2">
            <div className="flex items-center mb-1">
              <label
                htmlFor="invoicePaymentMethod"
                className="block text-sm font-medium text-gray-900"
              >
                Preferred Invoice Payment Method
              </label>
              <Tooltip content="Select how the member prefers to pay their invoice" />
            </div>
            <Select
              id="invoicePaymentMethod"
              name="invoicePaymentMethod"
              value={paymentInfo.paymentDetails.invoicePaymentMethod}
              onChange={handleInvoiceMethodChange}
              displayEmpty
              fullWidth
              error={!!errors.invoicePaymentMethod}
              sx={{
                backgroundColor: "white",
                borderRadius: 3,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#B0B5BC",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#B0B5BC",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 1,
                  borderColor: "#E1A731",
                },
                "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d32f2f",
                },
                "& .MuiSelect-select": {
                  fontSize: 16,
                },
              }}
            >
              <MenuItem value="" disabled>
                Select a payment method
              </MenuItem>
              {invoicePaymentMethods.map((method) => (
                <MenuItem key={method.value} value={method.value}>
                  {method.label}
                </MenuItem>
              ))}
            </Select>
            {errors.invoicePaymentMethod && (
              <p className="text-red-500 text-xs mt-1">
                {errors.invoicePaymentMethod}
              </p>
            )}
          </div>
        )}

        <FormControl component="fieldset" className="mt-6">
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={paymentInfo.paymentDetails.autoRenew}
                  onChange={handleCheckboxChange("autoRenew")}
                />
              }
              label={
                <span className="text-sm">
                  Automatically renew membership
                  <Tooltip content="The membership will be automatically renewed each year" />
                </span>
              }
            />
          </FormGroup>
          {errors.agreeToTerms && (
            <p className="text-red-500 text-xs mt-1">{errors.agreeToTerms}</p>
          )}
        </FormControl>
      </div>
    </FormSection>
  );
};

export default PaymentInfoSection;
